import React from 'react';
import './Pagination.css';

const ChevronLeft = () => (
  <svg
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M8 2L4 6l4 4" />
  </svg>
);

const ChevronRight = () => (
  <svg
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 2l4 4-4 4" />
  </svg>
);

const DoubleChevronLeft = () => (
  <svg
    viewBox="0 0 16 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10 2L6 6l4 4M5 2L1 6l4 4" />
  </svg>
);

const DoubleChevronRight = () => (
  <svg
    viewBox="0 0 16 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 2l4 4-4 4M11 2l4 4-4 4" />
  </svg>
);

const PremiumSpinner = () => (
  <svg className="pg-premium-spinner" viewBox="0 0 50 50">
    <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" strokeWidth="4" />
    <path
      fill="none"
      stroke="currentColor"
      strokeWidth="4"
      strokeLinecap="round"
      d="M25 5A20 20 0 0 1 45 25"
    />
  </svg>
);

function getPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, '…', total];
  if (current >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
  return [1, '…', current - 1, current, current + 1, '…', total];
}

const Pagination = ({
  columns = [],
  data = [],
  renderRow,
  emptyState,
  loading = false,
  onSort,
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage,
  onItemsPerPageChange,
  totalItems,
  itemsName = 'items',
  showJumpTo = false,
  showFirstLast = false,
  rowOptions = [10, 25, 50, 100],
  className = '',
  sortBy = null,
  sortOrder = null,
}) => {
  const [jumpValue, setJumpValue] = React.useState(String(currentPage));
  const [jumpError, setJumpError] = React.useState(false);

  React.useEffect(() => {
    setJumpValue(String(currentPage));
    setJumpError(false);
  }, [currentPage]);

  const handleNav = (page) => {
    const clamped = Math.min(Math.max(1, page), totalPages);
    onPageChange(clamped);
  };

  const handleJumpKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    const parsed = parseInt(jumpValue, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= totalPages) {
      setJumpError(false);
      onPageChange(parsed);
    } else {
      setJumpError(true);
    }
  };

  const handleJumpBlur = () => {
    setJumpValue(String(currentPage));
    setJumpError(false);
  };

  const pages = getPageNumbers(currentPage, totalPages);
  const safeTotalItems = Number(totalItems) || 0;
  const startItem = safeTotalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = safeTotalItems > 0 ? Math.min(currentPage * itemsPerPage, safeTotalItems) : 0;

  const ControlBar = (
    <div className="pagination-container">
      {safeTotalItems > 0 && (
        <span className="pagination-info">
          Showing{' '}
          <strong>
            {startItem}–{endItem}
          </strong>{' '}
          of <strong>{safeTotalItems.toLocaleString()}</strong> {itemsName}
        </span>
      )}

      <div className="pagination-right">
        {onItemsPerPageChange && (
          <>
            <div className="pagination-rows">
              Rows per page
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  onItemsPerPageChange(Number(e.target.value));
                  onPageChange(1);
                }}
              >
                {rowOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="pagination-sep" />
          </>
        )}

        <nav className="pagination-nav" aria-label="Pagination navigation">
          {showFirstLast && (
            <button
              className="pg-btn"
              onClick={() => handleNav(1)}
              disabled={currentPage <= 1 || loading}
              aria-label="First page"
            >
              <DoubleChevronLeft />
            </button>
          )}
          <button
            className="pg-btn"
            onClick={() => handleNav(currentPage - 1)}
            disabled={currentPage <= 1 || loading}
            aria-label="Previous page"
          >
            <ChevronLeft />
          </button>

          {pages.map((page, i) =>
            page === '…' ? (
              <span key={`ell-${i}`} className="pg-ellipsis">
                …
              </span>
            ) : (
              <button
                key={page}
                className={`pg-btn${page === currentPage ? ' active' : ''}`}
                onClick={() => page !== currentPage && handleNav(page)}
                disabled={loading}
                aria-label={`Page ${page}`}
                aria-current={page === currentPage ? 'page' : undefined}
              >
                {page}
              </button>
            )
          )}

          <button
            className="pg-btn"
            onClick={() => handleNav(currentPage + 1)}
            disabled={currentPage >= totalPages || loading}
            aria-label="Next page"
          >
            <ChevronRight />
          </button>
          {showFirstLast && (
            <button
              className="pg-btn"
              onClick={() => handleNav(totalPages)}
              disabled={currentPage >= totalPages || loading}
              aria-label="Last page"
            >
              <DoubleChevronRight />
            </button>
          )}
        </nav>

        {showJumpTo && totalPages > 7 && (
          <>
            <div className="pagination-sep" />
            <div className="pagination-jump">
              Go to
              <input
                type="number"
                className={`pg-jump-input${jumpError ? ' error' : ''}`}
                min="1"
                max={totalPages}
                value={jumpValue}
                disabled={loading}
                onChange={(e) => {
                  setJumpValue(e.target.value);
                  setJumpError(false);
                }}
                onKeyDown={handleJumpKeyDown}
                onBlur={handleJumpBlur}
                aria-label="Jump to page"
              />
              <span className="pg-jump-total">/ {totalPages.toLocaleString()}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );

  if (columns.length > 0) {
    return (
      <div className={`pagination-card ${className}`}>
        {/* Fixed Header */}
        <div className="pagination-header-wrap">
          <table className="pagination-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    style={{ width: col.width }}
                    className={[col.className, col.sortable ? 'sortable' : '']
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => !loading && col.sortable && onSort?.(col.key)}
                  >
                    <div className="pg-th-content">
                      {col.label}
                      {col.sortable && (
                        <i
                          className={`fas ${sortBy === col.key ? (String(sortOrder).toUpperCase() === 'ASC' ? 'fa-sort-down' : 'fa-sort-up') : 'fa-sort'}`}
                          style={{
                            marginLeft: 6,
                            fontSize: 10,
                            opacity: sortBy === col.key ? 1 : 0.4,
                            color: sortBy === col.key ? 'var(--color-accent, #2563eb)' : 'inherit',
                          }}
                        />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
          </table>
        </div>

        {/* Scrollable Body */}
        <div className="pagination-body-wrap">
          <table className="pagination-table">
            <thead className="pg-invisible-thead">
              <tr>
                {columns.map((col) => (
                  <th
                    key={`inv-${col.key}`}
                    style={{ width: col.width, padding: 0, height: 0, border: 'none' }}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr className="pagination-loading-row">
                  <td colSpan={columns.length} className="pagination-loading-cell">
                    <div className="pagination-loading">
                      <PremiumSpinner />
                      <span>Loading…</span>
                    </div>
                  </td>
                </tr>
              ) : data.length > 0 ? (
                data.map((item, idx) => (
                  <React.Fragment key={item.id ?? idx}>{renderRow?.(item, idx)}</React.Fragment>
                ))
              ) : (
                <tr className="pagination-empty-row">
                  <td colSpan={columns.length} className="pagination-empty-cell">
                    <div className="pagination-empty">
                      {emptyState ?? (
                        <>
                          <i className="fas fa-folder-open" />
                          <p>No {itemsName} found</p>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {ControlBar}
      </div>
    );
  }

  return ControlBar;
};

export default Pagination;
